import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ArrowLeft, RefreshCcw, Loader2 } from 'lucide-react';
import {
  getAiqSyncStatus, refreshAiqSync, getAiqCustomerReview, getAiqProductReview, getAiqReference,
} from '../../lib/api';
import { SyncStatusBar } from '../../components/accountsiq/SyncStatusBar';
import { HealthCheckPanel } from '../../components/accountsiq/HealthCheckPanel';
import { CustomerSyncTable } from '../../components/accountsiq/CustomerSyncTable';
import { ProductSyncTable } from '../../components/accountsiq/ProductSyncTable';
import { ReferenceDataPanel } from '../../components/accountsiq/ReferenceDataPanel';
import { errMsg } from '../../components/accountsiq/aiqMeta';

const count = (data, ...keys) => keys.reduce((n, k) => n + (data?.counts?.[k] || 0), 0);

export default function AccountsIQSync() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [products, setProducts] = useState(null);
  const [reference, setReference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, c, p, r] = await Promise.all([getAiqSyncStatus(), getAiqCustomerReview(), getAiqProductReview(), getAiqReference()]);
      setStatus(s.data);
      setCustomers(c.data);
      setProducts(p.data);
      setReference(r.data);
    } catch (e) {
      toast.error(errMsg(e, 'Could not load AccountsIQ sync'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await refreshAiqSync();
      const s = data.stats || {};
      toast.success(`Refreshed: ${s.customers} customers, ${s.stock_items} stock items, ${s.price_items} prices`);
    } catch (e) {
      toast.error(errMsg(e, 'Refresh from AccountsIQ failed'));
    } finally {
      setRefreshing(false);
      await load();
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 p-8 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Loading AccountsIQ sync…</div>;
  }

  return (
    <div className="space-y-5" data-testid="aiq-sync-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} data-testid="aiq-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Settings
      </Button>
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <RefreshCcw className="w-6 h-6 text-[#005F73]" /> AccountsIQ Sync
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Refresh pulls a fresh copy of AccountsIQ customers, products, prices, tax codes and currencies. It never changes app data on its own — every difference below waits for your decision.
        </p>
      </div>
      <SyncStatusBar status={status} refreshing={refreshing} onRefresh={refresh} onSettings={() => navigate('/admin/data-management/accountsiq')} />
      <HealthCheckPanel health={status?.health} alert={status} collapsible onRan={(h) => setStatus((s) => ({ ...s, health: h }))} />
      <Tabs defaultValue="customers">
        <TabsList>
          <TabsTrigger value="customers" data-testid="aiq-tab-customers">Customers <span className="ml-1.5 font-mono text-xs text-amber-700">{count(customers, 'differs', 'aiq_only', 'not_in_aiq', 'no_code') || ''}</span></TabsTrigger>
          <TabsTrigger value="products" data-testid="aiq-tab-products">Products <span className="ml-1.5 font-mono text-xs text-amber-700">{count(products, 'differs', 'aiq_only', 'not_in_aiq', 'no_code') || ''}</span></TabsTrigger>
          <TabsTrigger value="reference" data-testid="aiq-tab-reference">Tax, currency &amp; price lists</TabsTrigger>
        </TabsList>
        <TabsContent value="customers" className="pt-3"><CustomerSyncTable data={customers} onChanged={load} /></TabsContent>
        <TabsContent value="products" className="pt-3"><ProductSyncTable data={products} onChanged={load} /></TabsContent>
        <TabsContent value="reference" className="pt-3"><ReferenceDataPanel data={reference} /></TabsContent>
      </Tabs>
    </div>
  );
}
